<template>
  <Swiper id="swiper" v-if="!isWeb" :space-between="20" :grab-cursor="true" :autoplay="{ delay: 3000 }" :speed="400"
    :initial-slide="initialSlide" :slides-per-view="slidesPerView">
    <SwiperSlide v-for="(file, key) in files" :key="key" @swiper="setSwiperRef" class="swiperSlide">
      <PdfViewer v-if="getViewerType(file?.type ?? '') === 'pdf'" :key="key +
        wksHandler.$.task.uuid.value +
        wksHandler.$.activity.uuid.value +
        '_modal'
        " :source="file.source" id="modal" :zoomable="zoomable" :rotation="rotation" :is-scroll="true"
        :page="currentPage" @page-changed="handlePageChanged" />
      <VideoViewer v-if="getViewerType(file?.type ?? '') === 'video'" :source="file.source" :zoomable="false" />
      <ImageViewer v-if="getViewerType(file?.type ?? '') === 'image'" :source="file.source" :zoomable="zoomable"
        :rotation="rotation" />
      <AudioPlayer v-if="getViewerType(file?.type ?? '') === 'audio'" :source="file.source" :type="file.type"
        thumbnail="/assets/Soundwave.svg" :zoomable="false" />
      <span v-if="getViewerType(file?.type ?? '') === 'other'"></span>
    </SwiperSlide>
  </Swiper>

  <div class="viewer-wrapper" v-if="isWeb">
    <button v-if="enableWebCarousel && files.length > 1" type="button" class="carousel-nav carousel-nav-prev"
      title="Anterior" @pointerdown.stop @pointerup.stop @click.stop="goPrev">
      <ion-icon :icon="chevronBackOutline"></ion-icon>
    </button>

    <PdfViewer v-if="currentFile && getViewerType(currentFile.type ?? '') === 'pdf'" :source="currentFile.source"
      id="modal" :zoomable="zoomable" :page="currentPage" @page-changed="handlePageChanged" />
    <VideoViewer v-if="currentFile && getViewerType(currentFile.type) === 'video'" :source="currentFile.source"
      :zoomable="false" />
    <ImageViewer v-if="currentFile && getViewerType(currentFile.type) === 'image'" :source="currentFile.source"
      :zoomable="zoomable" :rotation="rotation" />
    <AudioPlayer v-if="currentFile && getViewerType(currentFile.type) === 'audio'" :show-thumb="false"
      :source="currentFile.source" :type="currentFile.type" thumbnail="/assets/Soundwave.svg" :zoomable="false" />
    <span v-if="currentFile && getViewerType(currentFile.type) === 'other'"></span>

    <button v-if="enableWebCarousel && files.length > 1" type="button" class="carousel-nav carousel-nav-next"
      title="Seguinte" @pointerdown.stop @pointerup.stop @click.stop="goNext">
      <ion-icon :icon="chevronForwardOutline"></ion-icon>
    </button>

    <div v-if="enableWebCarousel && files.length > 1" class="carousel-counter">
      {{ currentIndex + 1 }} / {{ files.length }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from "swiper/vue";
import { IonIcon } from "@ionic/vue";
import { chevronBackOutline, chevronForwardOutline } from "ionicons/icons";
import VideoViewer from "@/modules/a.worksheets/views/components/VideoViewer.vue";
import ImageViewer from "@/modules/a.worksheets/views/components/ImageViewer.vue";
import AudioPlayer from "@/modules/a.worksheets/views/components/AudioPlayer.vue";
import { IFile } from "@/modules/a.worksheets/stores/worksheetDataStore";
import PdfViewer from "@/globals/components/PdfViewer/PdfViewer.vue";
import { useWorksheetHandler } from "@/globals/helpers/worksheetHandler";
import { computed, ref, toRefs, watch } from "vue";
import { useDeviceStatus } from "@/stores/deviceStatus";
import {
  getViewerType,
  MimeType,
  resolveMimeType,
} from "@/globals/helpers/media/mimeTypeUtils";
import { IFileResolved, useIFileResolver } from "@/modules/a.worksheets/helpers/useIFileResolver";

type TSwiper = typeof Swiper;

const props = defineProps({
  files: { type: Array<IFileResolved>, default: () => [] },
  slidesPerView: { type: Number, default: () => 1 },
  zoomable: { type: Boolean, default: () => true },
  initialSlide: { type: Number, default: () => 0 },
  // opt-in: lets the web viewer navigate between multiple files (arrows + counter,
  // honoring initialSlide). Defaults to off so every existing caller keeps the
  // original behavior of always showing files[0] on web.
  enableWebCarousel: { type: Boolean, default: () => false },
});
const emit = defineEmits(["getPopUp", "getSwiper"]);
const wksHandler = useWorksheetHandler();
const setSwiperRef = (swiper: TSwiper) => emit("getSwiper", swiper);

const { isWeb } = toRefs(useDeviceStatus());

// only relevant to the web viewer-wrapper branch: the mobile Swiper handles its
// own current slide internally and already supports swiping between files
const currentIndex = ref(props.initialSlide ?? 0);
watch(
  () => props.initialSlide,
  (value) => {
    currentIndex.value = value ?? 0;
  },
);
const currentFile = computed(() => {
  if (!props.enableWebCarousel) return props.files?.[0];
  return props.files?.[currentIndex.value] ?? props.files?.[0];
});

function goPrev() {
  if (!props.files?.length) return;
  currentIndex.value = (currentIndex.value - 1 + props.files.length) % props.files.length;
}

function goNext() {
  if (!props.files?.length) return;
  currentIndex.value = (currentIndex.value + 1) % props.files.length;
}

const rotation = ref(0);
function rotate() {
  rotation.value += 90;
  if (rotation.value === 360) {
    rotation.value = 0;
  }
  console.log("rotation value: ", rotation.value);
}

defineExpose({
  rotate,
});

const currentPage = ref(1);


const handlePageChanged = (page: number) => {
  currentPage.value = page;
}

</script>

<style scoped>
#swiper {
  height: 100%;
  width: 100%;
}

.swiperSlide {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5%;
  overflow: auto;
  max-height: 100%;
  margin-top: auto;
  margin-bottom: auto;
}

.textfield {
  width: 100%;
}
</style>

<style>
#swiper .zoomer {
  display: flex;
  justify-content: center;
}

.viewer-wrapper {
  height: 100vh;
  height: 100dvh;
  max-height: 100vh;
  max-height: 100dvh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.carousel-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 0;
  background: rgba(0, 0, 0, 0.45);
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  z-index: 10;
}

.carousel-nav:hover {
  background: rgba(0, 0, 0, 0.65);
}

.carousel-nav-prev {
  left: 4%;
}

.carousel-nav-next {
  right: 4%;
}

.carousel-counter {
  position: absolute;
  bottom: 4%;
  left: 50%;
  transform: translateX(-50%);
  padding: 2px 10px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.45);
  color: #ffffff;
  font-size: 0.75rem;
  z-index: 10;
}
</style>
